import {
  IAudioResInfo,
  IChapterApi,
  IDetailInfo,
  IKeyframeItem,
  ISpeakerSummaryApi,
  ISummary,
  MeetingTitleData,
} from "@/types/summary";
import {
  ICompanyStaffInfo,
  IInvitedStaffInfo,
  IShareGetAudioDownloadUrlParams,
  IShareGetMeetingDetailParams,
  IShareGetMeetingSummaryParams,
  IShareSettingParam,
  MeetingSharesItem,
  ICheckRecordingFinishedRes,
} from "@/types/share";
import request from "@/services/simpleRequest";
import osRequest from "@/services/osRequest";
import http from "./http";
import { AxiosRequestConfig } from "axios";
import { PageReturn } from "@/types/common";
import { PermissionChoose } from "@/constants/share";

/**
 * 查询录屏是否完成
 * @param streamName
 * @returns
 */
export async function checkOpenRecordingFinished(streamName: string): Promise<ICheckRecordingFinishedRes> {
  const res = await http.get<ICheckRecordingFinishedRes>('/api/open/record-server/record/splitResult', { params: { streamName } });
  return { recordStatus: res.data.recordStatus, finishStatus: res.data.finishStatus };
}


// 获取音频文件下载链接
export function getShareAudioDownloadUrl(
  params: IShareGetAudioDownloadUrlParams,
  config?: AxiosRequestConfig<unknown>
): Promise<IAudioResInfo> {
  return request.get(`/api/open/record-server/meeting/record/download/url`, {
    params,
    ...config,
  });
}

// 获取录制详情
export function getShareMeetingDetail(
  params: IShareGetMeetingDetailParams,
  config?: AxiosRequestConfig<unknown>
): Promise<IDetailInfo> {
  const { recordingType, streamId, ...shareParams } = params;
  console.log(">>> getShareMeetingDetail");
  return request.get(
    `/api/open/record-server/meeting/detail/${recordingType}/${streamId}`,
    {
      params: shareParams,
      ...config,
    }
  );
}

// 会议纪要总结
export function getShareMeetingSummary(
  params: IShareGetMeetingSummaryParams,
  config?: AxiosRequestConfig<unknown>
): Promise<ISummary> {
  return request.post("/api/open/record-server/meeting/summary", params, {
    timeout: 1000 * 60 * 5, // 5分钟超时
    ...config,
  });
}

// 智能章节
export function getShareMeetingSmartChapters(
  params: IShareGetMeetingSummaryParams,
  config?: AxiosRequestConfig<unknown>
): Promise<IChapterApi> {
  return request.post(
    "/api/open/record-server/meeting/smart-chapters",
    params,
    {
      timeout: 1000 * 60 * 5, // 5分钟超时
      ...config,
    }
  );
}

// 发言人总结
export function getShareMeetingSpeakerSummary(
  params: IShareGetMeetingSummaryParams,
  config?: AxiosRequestConfig<unknown>
): Promise<ISpeakerSummaryApi> {
  return request.post(
    "/api/open/record-server/meeting/speaker-summary",
    params,
    {
      timeout: 1000 * 60 * 5, // 5分钟超时
      ...config,
    }
  );
}

// 校验密码
export function verifySharePassword(params: {
  shareId: string;
  password: string;
}) {
  const { shareId, password } = params;
  return request.get(
    `/api/open/record-server/meeting/share/verify?shareId=${shareId}&password=${password}`
  );
}

// 校验大板分享的密码
export function verifyOSSharePassword(params: {
  streamName: string;
  password: string;
}) {
  const { streamName, password } = params;
  return osRequest.get(
    `/api/open/record-server/share/verify?streamName=${streamName}&password=${password}`
  );
}

// 获取个人端分享范围
export function getShareScope(
  streamName: string
) {
  return http.get<{
    shareScope: PermissionChoose;
  }>(`api/open/record-server/meeting/share-scope?streamName=${streamName}`
  );
}

// 获取大板分享范围
export function getOSShareScope(
  streamName: string
) {
  return http.get<{ meetingRecordVisibility: string }>(
    `/api/open/meeting/schedule-flow/configs?streamName=${streamName}`
  );
}

// 上报分享浏览
export function subscribeShareRecord(shareId: string) {
  return http.post(`/api/auth/live-record/share/subscribe/${shareId}`, {});
}

// 设置是否允许创建副本
export function setRecordAllowBackup(params: {
  streamName: string;
  allowBackUp: boolean;
}) {
  return http.post(`/api/auth/live-record/backup/setting`, params);
}

// 保存分享
export function saveShare(params: { streamName: string; contentStep: string }) {
  return http.post<string>(`/api/auth/record-server/sync/record`, params);
}

// 获取订阅分享
export function getSubscribeShareInfo(streamName: string) {
  return http.get<{
    streamName: string;
    url: string;
    createdDate: string;
    subscriber: string;
  }>(`/api/auth/live-record/share/subscribe?streamName=${streamName}`);
}

// 获取分享关键帧
export function getShareKeyframes(streamName: string) {
  return http.get<IKeyframeItem[]>("/api/open/record-server/keyframes", {
    params: { streamName },
    timeout: 5 * 60 * 1000,
  });
}

// 获取会议标题
export function getShareMeetingTitle(
  streamName: string,
  reload = false,
  contentStep: string
) {
  return http.get<MeetingTitleData>(
    "/api/open/record-server/meeting/title-summary",
    { params: { streamName, reload, contentStep }, timeout: 5 * 60 * 1000 }
  );
}

// 创建板端的与我分享
export function saveShareForMeeting(params: { streamName: string }) {
  return http.post<string>(
    "/api/auth/live-record/actions/create-share",
    params
  );
}

// 获取会议共享人
export async function getMeetingShares(shareId: string) {
  try {
    const res = await http.get<MeetingSharesItem[]>(
      `api/open/record-server/meeting/share/${shareId}/members`
    );
    return res.data;
  } catch (error) {
    console.log('getMeetingShares error', error);
    return [];
  }
}

// 更新共享人权限
export function updateSharePermission(params: {
  shareId: string;
  identityId: string;
  sharePermission: string;
}) {
  return http.post<string>(
    "/api/auth/live-record/share/permission/reset",
    params
  );
}

// 移除共享人
export function updateShareRemove(params: {
  shareId: string;
  identityId: string;
}) {
  return http.post<string>("/api/auth/live-record/share/remove", params);
}

// 会议分享设置
export function updateShareSetting(params: IShareSettingParam) {
  return http.post<string>(
    "/api/auth/live-record/meeting/share/setting",
    params
  );
}

// 分页查询企业成员列表
export function getCompanyStaffIds(params: { shareId: string, keyword?: string, page: number, size: number }) {
  return http.get<PageReturn<ICompanyStaffInfo>>(
    "/api/auth/company/staffIds/page",
    { params }
  );
}

// 最近邀请的共享人
export function getRecentInviter(shareId: string) {
  return http.get<PageReturn<IInvitedStaffInfo>>(
    `api/auth/live-record/share/${shareId}/recent/inviter`
  );
}

// 邀请共享人
export function inviteShare(params: {
  shareId: string;
  identitySharePermissions: {
    identityId: string;
    sharePermission: string;
  }[]
}) {
  return http.post<string>("/api/auth/live-record/share/invite", params);
}

// 分享密码校验（鉴权）: 用于个人端分享为企业内可见时使用
export function verifyCompanyShare(params: { shareId: string; password: string }) {
  return http.get<string>(
    `api/auth/live-record/meeting/share/verify`, { params }
  );
}

// 获取是否允许创建副本配置
export async function getBackupSetting(params: { streamName: string }) {
  try {
    const res = await http.get<boolean>(
      `api/auth/live-record/backup/setting`, { params }
    );
    return res.data;
  } catch (error) {
    console.log('getBackupSetting error', error);
    return false;
  }
}

